import { ProposalAccessory } from './proposal-services.js';
export { scopedFloorArea, accessoryFloorScopes } from './proposal-accessory-scope.js';
/** Quantity belongs to the scope being covered, never to the catalogue UOM. */
export function scopedAreaQuantity(selection, m, areaId) {
    const raw = areaId ? m?.floors.find((floor) => floor.area_id === areaId)?.area_m2 : (m?.floorAreaM2 ?? 0);
    return Math.round((raw ?? 0) * 100) / 100;
}
/** Used by every scope picker: linear trims retain their measured run. */
export function accessoryScopePatch(row, selection, m, areaId) {
    return { scope: areaId ? 'area' : 'site', area_id: areaId,
        quantity_source: row.kind === 'trims' ? 'manual' : 'assessment',
        quantity: row.kind === 'trims' ? row.quantity : scopedAreaQuantity(selection, m, areaId) };
}
export function accessoryMeasurementUnit(kind, catalogueUnit) {
    return kind === 'scotia' || kind === 'smoothedge' || kind === 'trims' ? 'LM' : catalogueUnit || 'm²';
}
/** Carry a trim type already named by Mobile/canvas into the catalogue picker. */
export function assessedAccessoryTypeId(types, assessedKinds) {
    const normalise = (value) => value.toLowerCase().replace(/\btrims?\b/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
    const wanted = [...new Set(assessedKinds.map(normalise).filter((value) => value && value !== 'trim'))];
    if (wanted.length !== 1)
        return null;
    const matches = types.filter((type) => { const name = normalise(type.name); return name === wanted[0] || name.includes(wanted[0]); });
    return matches.length === 1 ? matches[0].product_id : null;
}
export function accessoryVariantsForFloor(kind, variants, manufacturerId) {
    if (kind !== 'scotia' && kind !== 'trims')
        return variants;
    return manufacturerId ? variants.filter((variant) => variant.supplier_id === manufacturerId) : [];
}
/** Render the actual supplier specification; supplier code alone hides different trim sizes. */
export function accessoryVariantLabel(v) {
    return [...new Set([v.supplier_name, v.label, v.spec, v.length_mm ? `${v.length_mm} mm` : null].filter(Boolean))].join(' · ');
}
/**
 * WHICH OPTION A TRIM STARTS ON (Jacky, 17 October 2026).
 *
 * *"the Trims dimension you only need to match the U Channel that is clamped in. all other Trims are universal used on
 * all board thickness"*. So only a U Channel is matched to the board; every other trim takes the floor supplier's own
 * option, the one marked default in the catalogue first (PACO's L trim: the 10x25mm).
 *
 * The catalogue's "Or equivalent" row (supplier OEM, source manual-equivalent) has no supplier of its own; the price list
 * lends it the trim product's supplier, so it passes the "same supplier as the floor" filter for every PACO floor. It is
 * the fallback, never the first pick: on Proposal 041c4596 the PACO Uni Cover started on OEM because OEM sorted first.
 */
export const isEquivalentTrim = (v) => v.source === 'manual-equivalent' || /^or equivalent\b/i.test(v.spec ?? '');
export function trimDefaultVariant(variants, boardThickness, matchThickness = true) {
    const own = variants.filter(v => !isEquivalentTrim(v)), pool = own.length ? own : variants;
    const plain = () => pool.find(v => v.is_default) ?? pool[0] ?? null;
    if (!matchThickness || !boardThickness)
        return plain();
    const hasSizes = pool.some(v => /\d+(?:\.\d+)?\s*mm\b/i.test(v.spec ?? ''));
    if (!hasSizes)
        return plain();
    const sized = pool.map(v => ({ v, size: Number(v.spec?.match(/(\d+(?:\.\d+)?)\s*mm\b/i)?.[1]) })).filter(x => Number.isFinite(x.size) && x.size >= boardThickness).sort((a, b) => a.size - b.size);
    return sized[0]?.v ?? null;
}
/** The U Channel clamps the board edge, so it is the one trim sized to the board. */
export const trimMatchesBoard = (typeName) => /\bu\s*-?\s*channel\b/i.test(typeName);
const FAMILY_ORDER = ['underlay', 'moisture_barrier', 'smoothedge', 'scotia', 'trims', 'adhesive', 'levelling', 'stair'];
export function floorAccessoryContext(product, selection) {
    const chosen = product.variants.find(v => v.id === selection.variantId);
    return { carpet: product.pricing.unit === 'LM', manufacturerId: chosen?.supplier_id ?? product.supplier_id ?? null,
        boardThickness: Number(chosen?.thickness ?? product.variants.find(v => v.thickness)?.thickness) || Number(product.name.match(/(\d+(?:\.\d+)?)\s*mm\b/i)?.[1]) || null };
}
/** The same eligible families and supplier specifications for web and native callers. */
export function floorAccessoryFamilies(catalogue, carpet, manufacturerId) {
    const grouped = new Map();
    for (const item of catalogue) {
        if (item.kind === 'other' || !item.kind)
            continue;
        if (carpet) {
            if (item.kind === 'underlay' && !/carpet underlay/i.test(item.name))
                continue;
            if (item.kind === 'scotia' && !/smooth edges?/i.test(item.name))
                continue;
            if (!['underlay', 'scotia'].includes(item.kind))
                continue;
        }
        const kind = carpet && item.kind === 'scotia' ? 'smoothedge' : item.kind;
        const variants = carpet ? item.variants : accessoryVariantsForFloor(item.kind, item.variants, manufacturerId);
        if (!variants.length)
            continue;
        if (!grouped.has(kind))
            grouped.set(kind, []);
        grouped.get(kind).push({ ...item, kind, variants });
    }
    const rank = (kind) => { const i = FAMILY_ORDER.indexOf(kind); return i < 0 ? FAMILY_ORDER.length : i; };
    return [...grouped.entries()].sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0])).map(([kind, types]) => ({ kind, types: types.sort((a, b) => a.name.localeCompare(b.name)) }));
}
export function floorAccessoryDefault(kind, type, boardThickness) {
    return kind === 'trims' ? trimDefaultVariant(type.variants, boardThickness, trimMatchesBoard(type.name)) : type.variants.find(v => v.is_default) ?? type.variants[0] ?? null;
}
/** Repeatable web rows: seed the next assessed underlay floor; trims start at zero measured LM. */
export function newRepeatedAccessory(input) {
    const { id, kind, fallbackName, productId, selection, accessories, measurements, types, boardThickness } = input;
    if (accessories.length >= 60 || accessories.some(a => a.id === id))
        throw Error('The accessory identity is already present or the Proposal is full.');
    const held = accessories.filter(a => a.kind === kind && a.bound_to === productId);
    const assessed = kind === 'underlay' ? measurements?.floors.find(f => f.underlay && !held.some(a => a.scope === 'area' && a.area_id === f.area_id)) : undefined;
    const type = types[0], variant = type ? floorAccessoryDefault(kind, type, boardThickness) : null;
    return ProposalAccessory.parse({ id, kind, name: type?.name ?? fallbackName, scope: assessed ? 'area' : 'site', area_id: assessed?.area_id ?? '', colour: null, on: true, bound_to: productId,
        unit: kind === 'trims' ? 'LM' : 'm²', quantity: kind === 'trims' ? 0 : scopedAreaQuantity(selection, measurements, assessed?.area_id ?? ''), quantity_source: kind === 'trims' ? 'manual' : 'assessment', rate: null, note: '', product_id: type?.product_id ?? null, variant_id: variant?.variant_id ?? null });
}
//# sourceMappingURL=accessory-choices.js.map